/**
 * usePracticeScope — the "Alle Praxen" toggle, as one piece of logic.
 *
 * Two layers, deliberately split:
 *
 *   • The user's INTENT is one boolean (`allPracticesMode`) living in
 *     PracticeContext, above <Routes>. Turn the toggle on from the Dashboard,
 *     walk to Tasks, and it is already on — the control is one implementation
 *     rendered in several places (see PracticeScopeToggle), not a lookalike
 *     button per page each holding its own state.
 *
 *   • What that intent RESOLVES TO is asked per page, because it legitimately
 *     differs. `forTasks` sets the endpoint's `for_tasks` flag, which admits
 *     task_manager memberships — a role that reaches the Tasks page and is 403'd
 *     on every other route. So a caller who is task_manager of one practice and
 *     owner of two pools three on Tasks and two on the Dashboard. For everyone
 *     without a mixed role (the ordinary case) both answers are identical.
 *
 * NOT `availablePractices`: that is the practice SWITCHER's answer and hands a
 * super_admin the whole fleet, which is right for switching and wrong for a pool.
 * The backend mirrors the practice on screen for a super_admin instead — see
 * dependencies/practice_scope.view_scope.
 *
 * Listing a practice here is not access to it. The caller then fetches each
 * practice through its own practice-scoped endpoint, which re-checks membership
 * and arms RLS for that practice alone, so nothing ever reads across tenants.
 */
import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import axios from '../utils/axiosInstance';
import { usePractice } from '../context/PracticeContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
// Module-level so the default never makes a new array identity per render.
const NO_PRACTICES = [];

export const usePracticeScope = ({ forTasks = false } = {}) => {
  const { practiceId, availablePractices, allPracticesMode, setAllPracticesMode } = usePractice();

  // Held with cacheTime Infinity: the answer is stable for the session, so the
  // toggle paints immediately on a revisit instead of blinking in once the
  // request lands. `forTasks` is part of the key — the two pages can get
  // different lists and must not share one cache entry.
  const { data: scopePractices = NO_PRACTICES } = useQuery(
    ['practice-scope', practiceId, availablePractices.map((p) => p.practice_id).join(','), forTasks],
    async () => {
      const res = await axios.get(`${API_BASE_URL}/users/me/practices/scope`, {
        params: { practice_id: practiceId, ...(forTasks ? { for_tasks: true } : {}) },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
    {
      enabled: !!practiceId,
      cacheTime: Infinity,
      // A failed scope lookup simply means no toggle. Nothing here is worth an
      // error banner over the whole page.
      retry: false,
      onError: () => {},
    }
  );

  // Sorted so the cache key is stable regardless of the order the API returned.
  const scopeIds = useMemo(() => scopePractices.map((p) => p.practice_id).sort(), [scopePractices]);
  const scopeKey = scopeIds.join(',');
  // Nothing to pool with a single practice, so the toggle never appears.
  const canAggregate = scopeIds.length > 1;

  // A page that cannot pool ignores the shared flag rather than acting on it —
  // so turning the toggle on where three practices are in scope never leaves a
  // one-practice page in a mode it has no data for.
  const allPractices = allPracticesMode && canAggregate;

  // The practices on screen (the pool, else the selected practice): a string for
  // effect deps, the ids to fan out over, and a query-key head that is exactly
  // [base, practiceId] in single mode, the key other pages already invalidate.
  const activeKey = allPractices ? scopeKey : (practiceId || '');
  const activeIds = useMemo(() => (activeKey ? activeKey.split(',') : []), [activeKey]);
  const scopedKey = useCallback((base) => [allPractices ? `${base}-all` : base, activeKey], [allPractices, activeKey]);

  // practice_id → display name, for views that label which practice a row came
  // from. Comes off the scope response, so it needs no second request.
  const practiceNameById = useMemo(() => {
    const byId = new Map();
    scopePractices.forEach((p) => byId.set(p.practice_id, p.practice_name || ''));
    return byId;
  }, [scopePractices]);

  return {
    scopeIds,
    scopeKey,
    canAggregate,
    allPractices,
    setAllPracticesMode,
    practiceNameById,
    activeKey,
    activeIds,
    scopedKey,
  };
};

export default usePracticeScope;
